import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PriceChart from "../components/PriceChart";
import { FlashCell, fmtMoney, fmtPrice, fmtPct } from "../components/Format";
import { Activity, TrendingUp, TrendingDown, DollarSign, Bot as BotIcon } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user, mode } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [stats, setStats] = useState(null);
  const [ticks, setTicks] = useState([]);
  const [bots, setBots] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [p, s, m, b] = await Promise.all([
        api.get("/portfolio"),
        api.get("/trades/stats", { params: { mode } }),
        api.get("/markets"),
        api.get("/bots"),
      ]);
      setPortfolio(p.data);
      setStats(s.data);
      setTicks(m.data);
      setBots(b.data);
    } catch { /* ignore transient refresh failures */ }
  }, [mode]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 2000);
    return () => clearInterval(id);
  }, [refresh]);

  const equity = mode === "live" ? portfolio?.equity_live : portfolio?.equity_paper;
  const cash = mode === "live" ? portfolio?.cash_live : portfolio?.cash_paper;
  const realizedPnl = mode === "live" ? portfolio?.realized_pnl_live : portfolio?.realized_pnl_paper;

  const featuredCrypto = ticks.find((t) => t.symbol === "BTC");
  const featuredStock = ticks.find((t) => t.symbol === "AAPL");
  const movers = [...ticks].sort((a, b) => Math.abs(b.change_pct_24h) - Math.abs(a.change_pct_24h)).slice(0, 6);

  const runningBots = bots.filter((b) => b.status === "running").length;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="label-xs">Hello {user?.name}</div>
          <h1 className="text-3xl font-[Chivo] tracking-tight">Mission Control</h1>
        </div>
        <div className="text-xs text-neutral-400">
          Active mode: <span className={`mono ${mode === "live" ? "text-[#FF3B30]" : "text-[#007AFF]"}`}>{mode.toUpperCase()}</span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border" data-testid="kpi-strip">
        <KPI icon={DollarSign} label="Equity" value={equity} format={(v)=>`$${fmtMoney(v)}`} testid="kpi-equity" />
        <KPI icon={Activity} label="Cash" value={cash} format={(v)=>`$${fmtMoney(v)}`} testid="kpi-cash" />
        <KPI
          icon={realizedPnl >= 0 ? TrendingUp : TrendingDown}
          label="Realized PnL"
          value={realizedPnl}
          format={(v)=>`${v>=0?"+":""}$${fmtMoney(v)}`}
          color={realizedPnl >= 0 ? "#00E676" : "#FF3B30"}
          testid="kpi-pnl"
        />
        <KPI icon={BotIcon} label="Running Bots" value={runningBots} format={(v)=>`${v} / ${bots.length}`} testid="kpi-bots" />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {featuredCrypto && (
            <ChartPanel symbol={featuredCrypto.symbol} tick={featuredCrypto} testid="chart-crypto" />
          )}
          {featuredStock && (
            <ChartPanel symbol={featuredStock.symbol} tick={featuredStock} testid="chart-stock" />
          )}
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="border border-border bg-[#0F0F0F]" data-testid="trade-stats-panel">
            <div className="px-4 py-3 border-b border-border flex justify-between">
              <div className="label-xs">Session Stats ({mode})</div>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <Row k="Total Trades" v={<span className="num">{stats?.trades ?? 0}</span>} />
              <Row k="Win Rate" v={<span className="num">{(stats?.win_rate ?? 0).toFixed(1)}%</span>} />
              <Row k="Net PnL" v={
                <span className={`num ${(stats?.pnl ?? 0) >= 0 ? "profit" : "loss"}`}>
                  {(stats?.pnl ?? 0) >= 0 ? "+" : ""}${fmtMoney(stats?.pnl ?? 0)}
                </span>}
              />
              <Row k="Fees Paid" v={<span className="num">${fmtMoney(stats?.fees ?? 0)}</span>} />
              <Row k="Volume" v={<span className="num">${fmtMoney(stats?.volume ?? 0, 0)}</span>} />
            </div>
          </div>

          <div className="border border-border bg-[#0F0F0F]" data-testid="movers-panel">
            <div className="px-4 py-3 border-b border-border flex justify-between">
              <div className="label-xs">Top Movers</div>
              <Link to="/markets" className="text-xs text-[#007AFF] hover:underline">View all</Link>
            </div>
            <div>
              {movers.map((t) => (
                <div key={t.symbol} className="flex items-center justify-between px-4 py-2 border-b border-border last:border-b-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${t.asset_class === "crypto" ? "bg-[#FFB300]" : "bg-[#007AFF]"}`} />
                    <span className="font-[Chivo] text-sm">{t.symbol}</span>
                  </div>
                  <div className="text-right">
                    <div className="num text-sm"><FlashCell value={t.price} format={fmtPrice} /></div>
                    <div className={`num text-xs ${t.change_pct_24h >= 0 ? "profit" : "loss"}`}>{fmtPct(t.change_pct_24h)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, format, color, testid }) {
  return (
    <div className="bg-[#0F0F0F] px-5 py-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-xs">{label}</div>
        <Icon size={14} className="text-neutral-500" />
      </div>
      <div className={`mt-2 text-2xl font-[Chivo] tracking-tight num`} style={{ color: color || "#fff" }}>
        {value === undefined ? "—" : format(value)}
      </div>
    </div>
  );
}

function ChartPanel({ symbol, tick, testid }) {
  return (
    <div className="border border-border bg-[#0F0F0F]" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="font-[Chivo] text-base">{symbol}</div>
          <div className="label-xs">{tick.asset_class}</div>
        </div>
        <div className="text-right">
          <div className="num text-lg"><FlashCell value={tick.price} format={(v)=>`$${fmtPrice(v)}`} /></div>
          <div className={`num text-xs ${tick.change_pct_24h >= 0 ? "profit" : "loss"}`}>{fmtPct(tick.change_pct_24h)} 24h</div>
        </div>
      </div>
      <PriceChart symbol={symbol} />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-neutral-400">{k}</span>
      {v}
    </div>
  );
}
